import { AdvancedImage, lazyload, placeholder } from '@cloudinary/react'
import { CloudinaryImage } from '@cloudinary/url-gen'
import { auto } from '@cloudinary/url-gen/qualifiers/format'
import { format } from '@cloudinary/url-gen/actions/delivery'

import { isPrint } from '@/utils/print'
import { cx } from '@/utils/strings'

export type ImageContentfulProps = {
  contentful_id?: string
  title: string
  description?: string
  publicUrl?: string
  file?: {
    contentType: string
    url: string
  }
  svg?: {
    content: string
  }
  className?: any
  loading?: 'eager' | 'lazy'
  invertSvgForDarkMode?: boolean
}

export default function ImageContentful({
  contentful_id,
  title,
  description,
  publicUrl,
  file,
  svg,
  loading = 'lazy',
  invertSvgForDarkMode = false,
  ...rest
}: ImageContentfulProps) {
  if (!file && !publicUrl && !svg?.content) return
  const caption = description ? `${title} - ${description}` : title
  // brand color hex values to ignore when inverting colors
  const ignoreList = ['2CDDE9', '2cdde9']
  function invertColor(hex) {
    //if hex is in ignoreList, return the original hex
    if (ignoreList.includes(hex)) {
      return '#' + hex
    }

    // Remove the # if it exists
    hex = hex.replace('#', '')

    // Convert the hex color to RGB
    let r = parseInt(hex.substr(0, 2), 16)
    let g = parseInt(hex.substr(2, 2), 16)
    let b = parseInt(hex.substr(4, 2), 16)

    // Invert the RGB values
    r = 255 - r
    g = 255 - g
    b = 255 - b

    // Convert the RGB values back to hex
    let invertedHex = '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)

    return invertedHex
  }

  if (svg?.content) {
    // find sting fill="#020F66" or fill="#020f66" and replace with fill="white"
    let newSvg = svg.content
    if (invertSvgForDarkMode) {
      newSvg = newSvg.replace(/fill="#020F66"/g, 'fill="white"').replace(/fill="#020f66"/g, 'fill="white"')
      newSvg = newSvg.replace(/stroke="#020F66"/g, 'stroke="white"').replace(/stroke="#020f66"/g, 'stroke="white"')

      // extract each fill hex value in newSvg and run it through the invertColor function
      const hexValues = newSvg.match(/fill="#[0-9A-Fa-f]{6}"/g)
      hexValues?.forEach((hexValue) => {
        const newHexValue = invertColor(hexValue.replace('fill="#', '').replace('"', ''))
        newSvg = newSvg.replace(hexValue, `fill="${newHexValue}"`)
      })
    }
    return <div dangerouslySetInnerHTML={{ __html: newSvg }} {...rest} className={cx('w-full', rest.className)} />
  }

  if (['image/svg+xml'].includes(file?.contentType ?? '') || isPrint()) {
    let src = publicUrl || file?.url
    if (import.meta.env.APP_ENV === 'local') src = file?.url || publicUrl
    return <img data-testid="image" src={src} alt={caption} {...rest} />
  }

  // use this to lazy migrate
  const path = file?.url?.replace('//images.ctfassets.net/', 'ctf/')?.replace('//images.contentful.com/', 'ctf/')

  const cldImg = new CloudinaryImage(path, {
    cloudName: 'bw-com',
  }).delivery(format(auto()))
  // use this after migration
  // const cld = new Cloudinary({
  //   cloud: {
  //     cloudName: 'bw-com',
  //   },
  // })
  // const cldImg = cld.image(contentful_id)

  return (
    <AdvancedImage
      cldImg={cldImg}
      plugins={[
        ...(loading === 'lazy' ? [lazyload()] : []),
        placeholder({ mode: 'blur' }),
        // for some reason responsive doesn't look quite sharp enough with some images
        // responsive({ steps: [600, 1000, 1400] }),
      ]}
      alt={caption}
      {...rest}
    />
  )
}
